import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import ContinueButton from "../../Registration/ContinueButton";
import { ChevronDown, ChevronRight } from "lucide-react";
import { setMembershipsCertifications, submitMembershipsCertifications } from "../../store/supplierFormDataSlice";


const categories = [
  {
    title: "Procurement & Supply Chain Management Organizations",
    options: [
      "American Purchasing Society (APS)",
      "Asia Pacific Procurement Confederation (APPC)",
      "Association for Supply Chain Management (ASCM)",
      "Australian Procurement and Construction Council (APCC)",
      "Canadian Public Procurement Council (CPPC)",
      "Chartered Institute of Procurement & Supply (CIPS)",
      "European Institute of Purchasing Management (EIPM)",
      "Institute for Supply Management (ISM)",
      "Institute of Supply Chain Management (IoSCM)",
      "International Association of Procurement Professionals (IAPP)",
      "International Federation of Purchasing and Supply Management (IFPSM)",
      "National Association of Educational Procurement (NAEP)",
      "National Institute of Governmental Purchasing (NIGP)",
      "Next Level Purchasing Association (NLPA)",
      "Procurement and Supply Chain Management Association of the Philippines (PASIA)",
      "Procurement Leaders",
      "Supply Chain Canada",
    ],
  },
  {
    title: "Government & Public Procurement Organizations",
    options: [
      "Institute for Public Procurement (NIGP)",
      "Institute of Governmental Purchasing (IGP)",
      "Higher Education",
      "National Association of State Procurement Officials (NASPO)",
      "National Procurement Institute (NPI)",
      "The Universal Public Procurement Certification Council (UPPCC)",
    ],
  },
  {
    title: "Contracting & Sourcing Organizations",
    options: [
      "Global Sourcing Association (GSA)",
      "International Association for Contract and Commercial Management (IACCM)",
      "National Contract Management Association (NCMA)",
      "World Commerce & Contracting (WorldCC)",
    ],
  },
  {
    title: "Small Business & Minority Business Organizations",
    options: [
      "Minority Business Development Agency (MBDA)",
      "National Association of Minority Contractors (NAMC)",
      "National Association of Small Business Contractors (NASBC)",
      "National Association of Women Business Owners (NAWBO)",
      "National Black Chamber of Commerce (NBCC)",
      "National Center for American Indian Enterprise Development (NCAIED)",
      "National Women Business Owners Corporation (NWBOC)",
      "U.S. Pan Asian American Chamber of Commerce (USPAACC)",
      "U.S. Small Business Administration (SBA)",
      "United States Hispanic Chamber of Commerce (USHCC)",
      "Asian/Pacific Islander American Chamber of Commerce & Entrepreneurship (ACE)",
    ],
  },
  {
    title: "State-Level Government & Administrative Organizations",
    options: [
      "National Association of State Auditors, Comptrollers and Treasurers (NASACT)",
      "National Association of State Budget Officers (NASBO)",
      "National Association of State Chief Administrators (NASCA)",
      "National Association of State Chief Information Officers (NASCIO)",
      "National Association of State Facilities Administrators (NASFA)",
      "National Association of State Personnel Executives (NASPE)",
      "National Association of State Technology Directors (NASTD)",
      "National Association of State Treasurers (NAST)",
      "National Association of State Workforce Agencies (NASWA)",
      "National Association of State Agencies for Surplus Property (NASASP)",
    ],
  },
  {
    title: "Logistics & Financial Infrastructure",
    options: [
      "International Warehouse Logistics Association (IWLA)",
      "Society for Worldwide Interbank Financial Telecommunication (SWIFT)",
    ],
  },
];



const MembershipCertificationsForm = ({ onNext, onSkip }) => {
  const [selectedCheckboxes, setSelectedCheckboxes] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const userId = useSelector((state) => state.formData.supplierId);
  const dispatch = useDispatch();
  const { membershipsCertifications } = useSelector((state) => state.formData);
  const { memberships = [], certifications = [] } = membershipsCertifications || {};

  const processMembershipsCertifications = () => {
    const expanded = {};
    const selected = {};

    const processItems = (items) => {
      items.forEach((item) => {
        const fullName = `${item.name} (${item.acronym})`;

        categories.forEach((category) => {
          if (category.options.includes(fullName)) {
            expanded[category.title] = true;
            selected[fullName] = true;
          }
        });
      });
    };

    processItems(memberships);
    processItems(certifications);

    setExpandedCategories(expanded);
    setSelectedCheckboxes(selected);
  };

  useEffect(() => {
    if (memberships.length > 0 || certifications.length > 0) {
      processMembershipsCertifications();
    }
  }, [memberships, certifications]);

  const handleCheckboxChange = (category, option, checked) => {
    setSelectedCheckboxes((prev) => ({
      ...prev,
      [option]: checked,
    }));
  };

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onNext();

    const extractAcronym = (option) => {
      const match = option.match(/\((.*?)\)$/);
      return match ? match[1] : ""; 
    };

    const membershipsData = categories
      .filter((category) => !category.title.includes("Certifications"))
      .flatMap((category) =>
        category.options
          .filter((option) => selectedCheckboxes[option])
          .map((name) => ({
            type: "membership",
            name: name.split(" (")[0].trim(),
            acronym: extractAcronym(name),
          }))
      );

    const certificationsData = categories
      .filter((category) => category.title.includes("Certifications"))
      .flatMap((category) =>
        category.options
          .filter((option) => selectedCheckboxes[option])
          .map((name) => ({
            type: "certification",
            name: name.split(" (")[0].trim(),
            acronym: extractAcronym(name),
          }))
      );

    const memberInfo = {
      id: userId,
      memberships: membershipsData,
      certifications: certificationsData,
    };

    dispatch(submitMembershipsCertifications({ memberInfo }));
    dispatch(setMembershipsCertifications(memberInfo));
    // onNext();
  };

  return (
    <section className="flex flex-col items-center min-h-screen justify-start bg-white py-10 px-0 sm:px-6">
      <header className="text-center mb-6">
        <h1 className="text-2xl font-semibold text-[#1D2639] lg:whitespace-nowrap">
          Membership Associations & Certifications
        </h1>
        <p className="text-base font-medium text-[#7E7E7E] w-full sm:w-[572px] mx-auto mt-2">
          List any industry associations you're a member of and relevant certifications you've obtained.
        </p>
      </header>

      <form
        className="flex flex-col flex-start justify-center items-start w-full sm:w-[572px] gap-6 p-6 rounded-lg"
        onSubmit={handleSubmit}
        aria-live="polite"
      >
        <fieldset className="border-none">
          <legend className="sr-only">Membership Associations & Certifications</legend>

          {categories.map((category) => (
            <div key={category.title} className="mb-8">
              <div
                className="flex items-center justify-start cursor-pointer mb-8"
                onClick={() => toggleCategory(category.title)}
              >
                {expandedCategories[category.title] ? <ChevronDown /> : <ChevronRight />}
                <h3 className="text-base text-start font-base">{category.title}</h3>
              </div>

              {expandedCategories[category.title] && (
                <ul>
                  {category.options.map((option) => (
                    <li key={option} className="flex items-center justify-start text-gray-500 whitespace-nowrap mb-2">
                      <input
                      className="checkbox-option flex items-center justify-start text-gray-500 mr-4"
                        type="checkbox"
                        checked={selectedCheckboxes[option] || false}
                        onChange={(e) =>
                          handleCheckboxChange(category.title, option, e.target.checked)
                        }
                      />
                      {option}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </fieldset>

        <ContinueButton onClick={handleSubmit} label="Save and Continue" />
        <button
          type="button"
          className="flex justify-center cursor-pointer items-center self-center w-full sm:w-[432px] h-14 p-5 
          rounded-[13px] text-center text-base font-medium border border-gray-300"
          onClick={onSkip}
        >
          Skip
        </button>
      </form>
    </section>
  );
};

export default MembershipCertificationsForm;
